import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  formatDistanceToNow,
  format,
  isToday,
  isYesterday,
  isThisWeek,
} from 'date-fns';
import EmailListView, { EmailListItem } from '../../../common/EmailListView';
import ErrorState from '../../../common/ErrorState';
import EmailComponent from './EmailComponent';
import {
  GetProspectScheduledEmailsResponse,
  ProspectScheduledEmail,
  GetProspectRepliesResponse,
  ProspectReplyEmail,
} from '@dreamai/types';
import api from '../../../../api';
import { ReactComponent as CircleInfoIcon } from '../../../../icons/circle-info.svg';
import { useDependencyUpdates } from '../../../../providers/DependencyUpdateProvider';

import '../../../common/email-list-view.scss';

interface ProspectEmailsViewProps {
  prospectId: number;
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

interface EmailViewState {
  mode: 'list' | 'loading' | 'email';
  selectedEmailId?: string;
  selectedEmailType?: 'scheduled' | 'reply';
  error?: string;
}

const ProspectEmailsView: React.FC<ProspectEmailsViewProps> = ({
  prospectId,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { registerCallback } = useDependencyUpdates();
  const [scheduledEmails, setScheduledEmails] = useState<
    ProspectScheduledEmail[]
  >([]);
  const [replies, setReplies] = useState<ProspectReplyEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewState, setViewState] = useState<EmailViewState>({ mode: 'list' });

  const fetchEmails = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Fetch both scheduled emails and replies in parallel
      const [scheduledResponse, repliesResponse] = await Promise.all([
        api.get<GetProspectScheduledEmailsResponse>(
          `/prospect/${prospectId}/scheduled-emails`,
        ),
        api.get<GetProspectRepliesResponse>(`/prospect/${prospectId}/replies`),
      ]);

      setScheduledEmails(scheduledResponse.data.emails);
      setReplies(repliesResponse.data.replies);
    } catch (err: any) {
      console.error('Error fetching emails:', err);
      setError(err.response?.data?.error || 'Failed to fetch emails');
    } finally {
      setLoading(false);
    }
  }, [prospectId, getAccessTokenSilently]);

  // Initial fetch on mount
  useEffect(() => {
    if (prospectId) {
      fetchEmails();
    }
  }, [prospectId, fetchEmails]);

  // Listen for dependency updates to scheduledEmails table
  useEffect(() => {
    if (!prospectId) return;

    // Register callback for ANY scheduledEmail update (we'll filter by prospect on refetch)
    const unregister = registerCallback('scheduledEmails', '', () => {
      console.log('Received scheduledEmails update, refetching...');
      fetchEmails();
    });

    return unregister;
  }, [prospectId, registerCallback, fetchEmails]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No date';
    try {
      const emailDate = new Date(dateString);

      if (isToday(emailDate)) {
        return formatDistanceToNow(emailDate, { addSuffix: true });
      }

      if (isYesterday(emailDate)) {
        return `Yesterday ${format(emailDate, 'HH:mm')}`;
      }

      if (isThisWeek(emailDate)) {
        return format(emailDate, 'EEE HH:mm');
      }

      return format(emailDate, 'HH:mm:ss, EEE, d MMM');
    } catch {
      return dateString;
    }
  };

  const getPillForScheduledEmail = (
    email: ProspectScheduledEmail,
  ): {
    text: string;
    variant:
      | 'default'
      | 'green'
      | 'red'
      | 'blue'
      | 'yellow'
      | 'light-blue'
      | 'beige';
  } => {
    if (email.sent) {
      return { text: 'Messaged', variant: 'blue' };
    }
    return { text: 'Scheduled', variant: 'yellow' };
  };

  const getPillForReply = (): {
    text: string;
    variant:
      | 'default'
      | 'green'
      | 'red'
      | 'blue'
      | 'yellow'
      | 'light-blue'
      | 'beige';
  } => {
    return { text: 'Reply', variant: 'green' };
  };

  // Handle clicking on an email to view details
  const handleEmailClick = async (item: EmailListItem) => {
    const emailId = item.id;

    try {
      setViewState({ mode: 'loading', selectedEmailId: emailId });

      let agentStorageEmailId: string | null = null;
      let emailType: 'reply' | 'scheduled' | null = null;

      if (emailId.startsWith('reply-')) {
        const replyId = emailId.replace('reply-', '');
        const reply = replies.find((r) => r.id.toString() === replyId);
        agentStorageEmailId = reply?.agentStorageEmailId || null;
        emailType = 'reply';
      } else if (emailId.startsWith('scheduled-')) {
        const scheduledId = emailId.replace('scheduled-', '');
        const scheduledEmail = scheduledEmails.find(
          (e) => e.id.toString() === scheduledId,
        );
        agentStorageEmailId = scheduledEmail?.sentAgentStorageEmailId || null;
        emailType = 'scheduled';
      }

      if (agentStorageEmailId) {
        setViewState({
          mode: 'email',
          selectedEmailId: agentStorageEmailId,
          selectedEmailType: emailType!,
        });
      } else {
        setViewState({
          mode: 'list',
          error: 'Email details not available',
        });
      }
    } catch (err) {
      console.error('Error loading email details:', err);
      setViewState({
        mode: 'list',
        error: 'Failed to load email details',
      });
    }
  };

  // Handle returning to the email list
  const handleBackToList = () => {
    setViewState({ mode: 'list' });
  };

  // Show individual email view
  if (viewState.mode === 'email' && viewState.selectedEmailId) {
    return (
      <div className='email-component-wrapper'>
        <div className='email-detail-header'>
          <button
            className='back-to-list-button'
            onClick={handleBackToList}
            type='button'
          >
            ← Back to List
          </button>
        </div>
        <div className='lower-wrapper'>
          <EmailComponent
            emailId={viewState.selectedEmailId}
            threadId={threadId || ''}
            updateThreadViewVisualDescription={
              updateThreadViewVisualDescription
            }
          />
        </div>
      </div>
    );
  }

  // Show loading state when loading email details
  if (viewState.mode === 'loading') {
    return (
      <div className='email-component-wrapper'>
        <div className='email-detail-header'>
          <button
            className='back-to-list-button'
            onClick={handleBackToList}
            type='button'
          >
            ← Back to List
          </button>
        </div>
        <div className='lower-wrapper'>
          <div className='loading-spinner-container'>
            <div className='loading-spinner'></div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className='prospect-emails-view loading-view'>
        <div className='placeholder-email'>
          <div className='placeholder-subject'></div>
          <div className='placeholder-body'></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='prospect-emails-view'>
        <ErrorState
          title='Failed to load emails'
          subtitle='Please try again later or refresh'
        />
      </div>
    );
  }

  if (viewState.error) {
    return (
      <div className='prospect-emails-view'>
        <ErrorState title='Error' subtitle={viewState.error} />
      </div>
    );
  }

  // Transform scheduled emails to EmailListItem format
  const scheduledEmailItems: EmailListItem[] = scheduledEmails.map((email) => ({
    id: `scheduled-${email.id}`,
    pill: getPillForScheduledEmail(email),
    address: `${email.prospectName} <${email.prospectEmail}>`,
    date: email.sentTime ? (
      formatDate(email.sentTime)
    ) : (
      <span className='email-date-with-tooltip'>
        Waiting to send
        <span className='tooltip-wrapper'>
          <CircleInfoIcon className='info-icon' />
          <span className='tooltip-text'>
            This email is ready to send and will be sent soon. We balance your
            queue to add a mix of new outbound and follow-ups.
          </span>
        </span>
      </span>
    ),
    subject: email.subject,
    snippet: email.snippet,
    sortDate: email.sentTime || email.scheduledTime || '',
  }));

  // Transform replies to EmailListItem format
  const replyItems: EmailListItem[] = replies.map((reply) => ({
    id: `reply-${reply.id}`,
    pill: getPillForReply(),
    address: reply.from,
    date: formatDate(reply.receivedAt),
    subject: reply.subject,
    snippet: reply.snippet,
    sortDate: reply.receivedAt,
  }));

  // Blend both lists together and sort by date (most recent first)
  const allEmailItems = [...scheduledEmailItems, ...replyItems].sort((a, b) => {
    const dateA = new Date(a.sortDate || 0).getTime();
    const dateB = new Date(b.sortDate || 0).getTime();
    return dateB - dateA; // Descending order (newest first)
  });

  if (allEmailItems.length === 0) {
    return (
      <div className='prospect-emails-view'>
        <p>No emails found for this prospect.</p>
      </div>
    );
  }

  return (
    <div className='prospect-emails-view'>
      <EmailListView
        items={allEmailItems}
        showPills={true}
        emptyHeader='No emails'
        emptyText={`We haven't sent this target any emails yet.`}
        onItemClick={handleEmailClick}
      />
    </div>
  );
};

export default ProspectEmailsView;
